import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

function StatCard({ label, value, sub, testid, tone }) {
  const border =
    tone === "positif"
      ? "border-emerald-600"
      : tone === "negatif"
        ? "border-red-600"
        : "border-neutral-200";
  const accent =
    tone === "positif"
      ? "text-emerald-700"
      : tone === "negatif"
        ? "text-red-700"
        : "text-neutral-900";
  return (
    <div className={`border ${border} p-6 bg-white`} data-testid={testid}>
      <div className="label-overline">{label}</div>
      <div className={`font-display font-black text-5xl mt-3 tracking-tighter leading-none ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) {
    return (
      <div className="p-6 md:p-10">
        <div className="label-overline">Memuat...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 md:mb-12">
        <div className="label-overline">Ringkasan</div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] mt-2">
          Dashboard
        </h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-6">
        <StatCard label="Total Objek Mad’u" value={stats.total_pesantren} testid="stat-total-pesantren" />
        <StatCard label="Total Kunjungan" value={stats.total_kunjungan} sub={`${stats.kunjungan_bulan_ini} bulan ini`} testid="stat-total-kunjungan" />
        <div className="col-span-2 border border-black bg-black text-white p-6 flex flex-col justify-between">
          <div>
            <div className="label-overline text-neutral-400">Aksi Cepat</div>
            <div className="font-display font-black text-2xl mt-2 tracking-tighter">Tambah data baru</div>
          </div>
          <div className="flex gap-2 mt-4">
            <Link
              to="/pesantren"
              data-testid="quick-add-pesantren"
              className="flex-1 border border-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-colors duration-150 flex items-center justify-between"
            >
              Objek Mad’u <ArrowRight size={14} />
            </Link>
            <Link
              to="/kunjungan"
              data-testid="quick-add-kunjungan"
              className="flex-1 border border-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-colors duration-150 flex items-center justify-between"
            >
              Kunjungan <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      <div className="mb-8 md:mb-12">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="label-overline">Kategori</div>
            <h2 className="font-display font-bold text-xl md:text-2xl tracking-tight mt-1">Sebaran per Kategori</h2>
          </div>
          <Link
            to="/pengaturan"
            data-testid="dashboard-manage-categories"
            className="text-xs uppercase tracking-wider border-b border-black hover:opacity-60"
          >
            Kelola Kategori
          </Link>
        </div>
        {stats.by_category.length === 0 ? (
          <div className="border border-neutral-200 p-6 text-center text-sm text-neutral-500">
            Belum ada kategori. <Link to="/pengaturan" className="underline">Tambah kategori</Link> untuk mulai.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {stats.by_category.map((c) => (
              <StatCard
                key={c.id}
                label={c.name}
                value={c.count}
                sub={c.polarity === "positif" ? "Positif" : "Negatif"}
                tone={c.polarity}
                testid={`stat-cat-${c.name}`}
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="label-overline">Aktivitas</div>
            <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-1">Kunjungan Terbaru</h2>
          </div>
          <Link
            to="/kunjungan"
            data-testid="view-all-kunjungan"
            className="text-xs uppercase tracking-wider border-b border-black hover:opacity-60"
          >
            Lihat semua
          </Link>
        </div>

        {stats.recent_kunjungan.length === 0 ? (
          <div className="border border-neutral-200 p-8 text-center text-neutral-500 text-sm">
            Belum ada kunjungan tercatat.
          </div>
        ) : (
          <div className="border border-neutral-200 divide-y divide-neutral-200">
            {stats.recent_kunjungan.map((k) => (
              <div key={k.id} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-base tracking-tight truncate">{k.pesantren_nama}</div>
                  <div className="text-xs text-neutral-500 mt-1 truncate">{k.topik}</div>
                </div>
                <div className="text-xs text-neutral-600 whitespace-nowrap">
                  {new Date(k.tanggal).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
